import {createContext, useCallback, useContext, useState} from 'react';
import PropTypes from 'prop-types';

const TableContext = createContext({
  cellAlign: 'left',
  storageKey: undefined,
  columnWidths: {},
  setColumnWidth: () => {},
  setColumnWidths: () => {},
  ensureColumnsMeasured: () => {},
  headerLabels: [],
  mobileSections: false,
});

export const useTableContext = () => useContext(TableContext);

export const TableProvider = ({
  cellAlign,
  storageKey,
  columnIds,
  fixedColumnWidths,
  tableRef,
  headerLabels = [],
  mobileSections = false,
  children,
}) => {
  const [columnWidths, setColumnWidthsState] = useState(() => {
    if (!storageKey) {
      return {};
    }
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const persist = useCallback((widths) => {
    if (!storageKey) {
      return;
    }
    try {
      localStorage.setItem(storageKey, JSON.stringify(widths));
    } catch {
      // ignore
    }
  }, [storageKey]);

  const setColumnWidth = useCallback((columnId, width, shouldPersist = false) => {
    setColumnWidthsState(prev => {
      const next = {...prev, [columnId]: width};
      if (shouldPersist) {
        persist(next);
      }
      return next;
    });
  }, [persist]);

  const setColumnWidths = useCallback((updates, shouldPersist = false) => {
    setColumnWidthsState(prev => {
      const next = {...prev, ...updates};
      if (shouldPersist) {
        persist(next);
      }
      return next;
    });
  }, [persist]);

  const ensureColumnsMeasured = useCallback(() => {
    if (!columnIds || columnIds.length === 0 || !tableRef?.current) {
      return;
    }
    const measured = {};
    columnIds.forEach(id => {
      if (fixedColumnWidths && fixedColumnWidths[id] !== undefined) {
        return;
      }
      if (columnWidths[id] !== undefined) {
        return;
      }
      const th = tableRef.current.querySelector(`th[data-column-id="${id}"]`);
      if (th) {
        measured[id] = Math.round(th.getBoundingClientRect().width);
      }
    });
    if (Object.keys(measured).length > 0) {
      setColumnWidthsState(prev => ({...prev, ...measured}));
    }
  }, [columnIds, columnWidths, tableRef, fixedColumnWidths]);

  return (
    <TableContext.Provider value={{cellAlign, storageKey, columnWidths, setColumnWidth, setColumnWidths, ensureColumnsMeasured, headerLabels, mobileSections}}>
      {children}
    </TableContext.Provider>
  );
};

TableProvider.propTypes = {
  cellAlign: PropTypes.oneOf(['left', 'center', 'right']),
  storageKey: PropTypes.string,
  columnIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  fixedColumnWidths: PropTypes.object,
  tableRef: PropTypes.shape({current: PropTypes.any}),
  headerLabels: PropTypes.arrayOf(PropTypes.string),
  mobileSections: PropTypes.bool,
  children: PropTypes.node,
};

export default TableContext;
